import { Tooltip, Typography } from "@mui/material";
import { DeleteOutline, Restore } from "@mui/icons-material";
import { FormizoCustom, FormizoSchema } from "Components/LabIZO/Formizo/FormizoSchema.interface";
import { HStack } from "Components/LabIZO/Stackizo";
import { StyledButton } from "Components/LabIZO/Stylizo";
import { ColorX, store } from "static";
import { MaintenancePageSetting } from "./schema/MaintenancePageSetting-schema";
import { LiveAgentSetting } from "./schema/LiveAgentSetting-schema";
import { InputFieldSetting } from "./schema/InputFieldSetting-schema";
import { SurveySetting } from "./schema/SurveySetting-schema";
import { SpamSetting } from "./schema/SpamSetting.schema";
import { TimeoutSetting } from "./schema/TimeoutSetting-schema";
import { RedirectURLSetting } from "./schema/RedirectURLSetting-schema";
import { EServiceSetting } from "./schema/EServiceSetting-schema";
import { AnswerReviewSetting } from "./schema/AnswerReviewSetting-schema";
import { ErrorMessageSetting } from "./schema/ErrorMessageSetting-schema";
import { SpecialAnswerSetting } from "./schema/SpecialAnswerSetting-schema";

const restore = [
  {
    label: "Version",
    name: "name",
  },
  {
    label: " ",
    name: "_id",
    width: 120,
    Cell: (row: object, field: object, addOns: { Restore: (feild: object) => void }) => {
      return (
        <StyledButton
          onClick={() => {
            if (addOns.Restore) {
              addOns.Restore(field);
            }
          }}
          theme={{
            width: 100,
            height: 25,
            color: ColorX.GetColorCSS("Decorate2"),
            hover: {
              background: ColorX.GetColorCSS("Decorate2"),
              color: ColorX.GetColorCSS("Decorate2"),
            },
          }}
        >
          <HStack gap={5}>
            <Restore fontSize="small" />
            <Typography style={{ fontSize: 12 }}>Restore</Typography>
          </HStack>
        </StyledButton>
      );
    },
  },
  {
    label: " ",
    name: "",
    width: 50,
    Cell: (row: { _id: string }, field: any, addOns: { Delete: (_id: string) => void }) => {
      return (
        <Tooltip title="Delete" arrow={true} placement="top">
          <StyledButton
            onClick={() => {
              if (addOns.Delete) {
                addOns.Delete(row._id);
              }
            }}
            theme={{
              width: 25,
              minWidth: 25,
              height: 25,
              color: "red",
              hover: {
                background: ColorX.GetColorCSS("Cancel"),
                color: ColorX.GetColorCSS("Cancel"),
              },
            }}
          >
            <HStack gap={5}>
              <DeleteOutline fontSize="small" />
            </HStack>
          </StyledButton>
        </Tooltip>
      );
    },
  },
];

const mwconfig = [
  {
    label: "Chatbot Name",
    name: "cbname",
    format: "text",
  },
  {
    label: "Disabe Menu Button",
    name: "MenuButtonDisable",
    format: "bool",
    boolStyle: "switch",
  },
  {
    label: "Icon URL",
    name: "avatar",
    format: "text",
  },
  {
    label: "Ambiguous Handling",
    name: "SResolve",
    format: "bool",
    boolStyle: "switch",
  },
  {
    label: "Welcome Message",
    name: "WelcomeMessage",
    format: "bool",
    boolStyle: "switch",
  },
  {
    label: "Suggested Questions",
    name: "FollowingIntent",
    format: "bool",
    boolStyle: "switch",
  },
  {
    label: "Number of Suggested Questions",
    name: "FollowingIntentNumber",
    format: "number",
  },
  {
    header: "Confidence Level",
  },
  {
    label: "High:        >=",
    name: "CFLevel.HIGH",
    format: "number",
  },
  {
    label: "Change:         <",
    name: "CFLevel.CHANGE",
    format: "number",
  },
  {
    label: "Mid:   <",
    name: "CFLevel.MID",
    format: "number",
  },
  {
    label: "Vague:   <",
    name: "CFLevel.VAGUE",
    format: "number",
  },
  {
    header: "Message Word Display Limit",
  },
  {
    label: "EN",
    name: "WordLimit.EN",
    format: "number",
  },
  {
    label: "TC",
    name: "WordLimit.TC",
    format: "number",
  },
  {
    label: "SC",
    name: "WordLimit.SC",
    format: "number",
  },
];

interface ILangSetting {
  caption: string;
  value: string;
}
interface IAddOns {
  lang: ILangSetting[];
}

const general: FormizoCustom = (formValue: any, addOns: IAddOns) => {
  addOns.lang = store.sysInfo.Language.available.map((o: { display: string; value: string }) => {
    return { caption: o.display, value: o.value };
  });
  return [
    {
      label: "Default Language",
      name: "Language.default",
      format: "select",
      selectStyle: "dropdown",
      selectRef: "lang",
      selectCap: "caption",
      selectVal: "value",
    },
    {
      label: "System Name",
      name: "Name",
      format: "text",
    },
  ] as FormizoSchema[];
};

const openaiCompletionConfig: FormizoSchema = {
  control: "completion.type",
  controlFunc: (doc, field) => doc?.completion?.type === "openai",
  fold: [
    {
      label: "Model",
      name: "completion.model",
      format: "text",
    },
    {
      label: "Completion API",
      name: "completion.completion_api",
      format: "text",
    },
    {
      label: "API Key",
      name: "completion.api_key",
      format: "text",
    },
    {
      label: "Base",
      name: "completion.base",
      format: "text",
    },
  ],
};

const azureCompletionConfig: FormizoSchema = {
  control: "completion.type",
  controlFunc: (doc, field) => doc?.completion?.type === "azure",
  fold: [
    {
      label: "Model",
      name: "completion.model",
      format: "text",
    },
    {
      label: "Completion API",
      name: "completion.completion_api",
      format: "text",
    },
    {
      label: "API Key",
      name: "completion.api_key",
      format: "text",
    },
    {
      label: "Base",
      name: "completion.base",
      format: "text",
    },
  ],
};

const openEmbeddingConfig: FormizoSchema = {
  control: "embeddings.type",
  controlFunc: (doc, field) => doc?.embeddings?.type === "openai",
  fold: [
    {
      label: "Model",
      name: "embedding.model",
      format: "text",
    },
    {
      label: "API Key",
      name: "embedding.api_key",
      format: "text",
    },
    {
      label: "Base",
      name: "embedding.base",
      format: "text",
    },
  ],
};

const azureEmbeddingConfig: FormizoSchema = {
  control: "embeddings.type",
  controlFunc: (doc, field) => doc?.embeddings?.type === "azure",
  fold: [
    {
      label: "Model",
      name: "embedding.model",
      format: "text",
    },
    {
      label: "API Key",
      name: "embedding.api_key",
      format: "text",
    },
    {
      label: "Base",
      name: "embedding.base",
      format: "text",
    },
  ],
};
const modelConfig: FormizoSchema[] = [
  {
    label: "Embedding Model Settings",
    reqAuth: "System.GPT.ModelConfig",
    foldStyle: "outlined",
    defaultShow: false,
    collapse: [
      {
        label: "Type",
        name: "embeddings.type",
        format: "select",
        selectStyle: "dropdown",
        validate: ["required"],
        options: [
          { value: "openai", label: "OpenAI" },
          { value: "azure", label: "Azure" },
        ],
        defaultValue: "azure",
      },
      openEmbeddingConfig,
      azureEmbeddingConfig,
    ],
  },
  {
    label: "Completion Model Settings",
    reqAuth: "System.GPT.ModelConfig",
    foldStyle: "outlined",
    defaultShow: false,
    collapse: [
      {
        label: "Type",
        name: "completion.type",
        format: "select",
        selectStyle: "dropdown",
        validate: ["required"],
        options: [
          { value: "openai", label: "OpenAI" },
          { value: "azure", label: "Azure" },
        ],
        defaultValue: "azure",
      },
      openaiCompletionConfig,
      azureCompletionConfig,
    ],
  },
];

const defaultMessage: FormizoSchema[] = [
  {
    label: "Default message ID",
    name: "welcomeDoc_morning._id",
    format: "text",
    variant: "outlined",
    readOnly: true,
  },
  {
    label: "Enable expiriable Message",
    name: "welcomeDoc_morning.expiriable.enable",
    format: "bool",
  },
  {
    label: "Enable From",
    name: "welcomeDoc_morning.expiriable.from",
    format: "date",
    dateType: "datetime",
    dateFormat: "YYYY-MM-DD HH:mm:ss",
  },
  {
    label: "Enable To",
    name: "welcomeDoc_morning.expiriable.to",
    format: "date",
    dateType: "datetime",
    dateFormat: "YYYY-MM-DD HH:mm:ss",
  },
  {
    label: "Message Type",
    name: "welcomeDoc_morning.answer.type",
    format: "select",
    selectStyle: "radio",
    selectDirection: "column",
    options: [
      { label: "Default Message", value: "default" },
      { label: "Expired Message", value: "expired" },
    ],
    defaultValue: "default",
  },
  {
    label: "welcomeDoc_morning.answer.type",
    control: "welcomeDoc_morning.answer.type",
    controlFunc: (doc: any, field: any) => field === "default",
    fold: [
      {
        label: "Text (EN)",
        name: "welcomeDoc_morning.text.EN",
        variant: "standard",
        format: "textarea",
      },
      {
        label: "Text (TC)",
        name: "welcomeDoc_morning.text.TC",
        variant: "standard",
        format: "textarea",
      },
      {
        label: "Text (SC)",
        name: "welcomeDoc_morning.text.SC",
        variant: "standard",
        format: "textarea",
      },
    ],
  },
  {
    label: "welcomeDoc_morning.answer.type",
    control: "welcomeDoc_morning.answer.type",
    controlFunc: (doc: any, field: any) => field === "expired",
    fold: [
      {
        label: "Text (EN)",
        name: "welcomeDoc_morning.expiriable.text.EN.0",
        variant: "standard",
        format: "textarea",
      },
      {
        label: "Text (TC)",
        name: "welcomeDoc_morning.expiriable.text.TC.0",
        variant: "standard",
        format: "textarea",
      },
      {
        label: "Text (SC)",
        name: "welcomeDoc_morning.expiriable.text.SC.0",
        variant: "standard",
        format: "textarea",
      },
    ],
  },
  {
    label: "Default message ID",
    name: "welcomeDoc_afternoon._id",
    format: "text",
    variant: "outlined",
    readOnly: true,
  },
  {
    label: "Enable expiriable Message",
    name: "welcomeDoc_afternoon.expiriable.enable",
    format: "bool",
  },
  {
    label: "Enable From",
    name: "welcomeDoc_afternoon.expiriable.from",
    format: "date",
    dateType: "datetime",
    dateFormat: "YYYY-MM-DD HH:mm:ss",
  },
  {
    label: "Enable To",
    name: "welcomeDoc_afternoon.expiriable.to",
    format: "date",
    dateType: "datetime",
    dateFormat: "YYYY-MM-DD HH:mm:ss",
  },
  {
    label: "Message Type",
    name: "welcomeDoc_afternoon.answer.type",
    format: "select",
    selectStyle: "radio",
    selectDirection: "column",
    options: [
      { label: "Default Message", value: "default" },
      { label: "Expired Message", value: "expired" },
    ],
    defaultValue: "default",
  },
  {
    label: "answer.type",
    control: "welcomeDoc_afternoon.answer.type",
    controlFunc: (doc: any, field: any) => field === "default",
    fold: [
      {
        label: "Text (EN)",
        name: "welcomeDoc_afternoon.text.EN",
        variant: "standard",
        format: "textarea",
      },
      {
        label: "Text (TC)",
        name: "welcomeDoc_afternoon.text.TC",
        variant: "standard",
        format: "textarea",
      },
      {
        label: "Text (SC)",
        name: "welcomeDoc_afternoon.text.SC",
        variant: "standard",
        format: "textarea",
      },
    ],
  },
  {
    label: "answer.type",
    control: "welcomeDoc_afternoon.answer.type",
    controlFunc: (doc: any, field: any) => field === "expired",
    fold: [
      {
        label: "Text (EN)",
        name: "welcomeDoc_afternoon.expiriable.text.EN.0",
        variant: "standard",
        format: "textarea",
      },
      {
        label: "Text (TC)",
        name: "welcomeDoc_afternoon.expiriable.text.TC.0",
        variant: "standard",
        format: "textarea",
      },
      {
        label: "Text (SC)",
        name: "welcomeDoc_afternoon.expiriable.text.SC.0",
        variant: "standard",
        format: "textarea",
      },
    ],
  },
  {
    label: "Default message ID",
    name: "welcomeDoc_evening._id",
    format: "text",
    variant: "outlined",
    readOnly: true,
  },
  {
    label: "Enable expiriable Message",
    name: "welcomeDoc_evening.expiriable.enable",
    format: "bool",
  },
  {
    label: "Enable From",
    name: "welcomeDoc_evening.expiriable.from",
    format: "date",
    dateType: "datetime",
    dateFormat: "YYYY-MM-DD HH:mm:ss",
  },
  {
    label: "Enable To",
    name: "welcomeDoc_evening.expiriable.to",
    format: "date",
    dateType: "datetime",
    dateFormat: "YYYY-MM-DD HH:mm:ss",
  },
  {
    label: "Message Type",
    name: "welcomeDoc_evening.answer.type",
    format: "select",
    selectStyle: "radio",
    selectDirection: "column",
    options: [
      { label: "Default Message", value: "default" },
      { label: "Expired Message", value: "expired" },
    ],
    defaultValue: "default",
  },
  {
    label: "welcomeDoc_evening.answer.type",
    control: "welcomeDoc_evening.answer.type",
    controlFunc: (doc: any, field: any) => field === "default",
    fold: [
      {
        label: "Text (EN)",
        name: "welcomeDoc_evening.text.EN",
        variant: "standard",
        format: "textarea",
      },
      {
        label: "Text (TC)",
        name: "welcomeDoc_evening.text.TC",
        variant: "standard",
        format: "textarea",
      },
      {
        label: "Text (SC)",
        name: "welcomeDoc_evening.text.SC",
        variant: "standard",
        format: "textarea",
      },
    ],
  },
  {
    label: "welcomeDoc_evening.answer.type",
    control: "welcomeDoc_evening.answer.type",
    controlFunc: (doc: any, field: any) => field === "expired",
    fold: [
      {
        label: "Text (EN)",
        name: "welcomeDoc_evening.expiriable.text.EN.0",
        variant: "standard",
        format: "textarea",
      },
      {
        label: "Text (TC)",
        name: "welcomeDoc_evening.expiriable.text.TC.0",
        variant: "standard",
        format: "textarea",
      },
      {
        label: "Text (SC)",
        name: "welcomeDoc_evening.expiriable.text.SC.0",
        variant: "standard",
        format: "textarea",
      },
    ],
  },
  {
    label: "Default message ID",
    name: "noanswerDoc._id",
    format: "text",
    variant: "outlined",
    readOnly: true,
  },
  {
    label: "Text (EN)",
    name: "noanswerDoc.text.EN",
    variant: "standard",
    format: "textarea",
  },
  {
    label: "Text (TC)",
    name: "noanswerDoc.text.TC",
    variant: "standard",
    format: "textarea",
  },
  {
    label: "Text (SC)",
    name: "noanswerDoc.text.SC",
    variant: "standard",
    format: "textarea",
  },
];

const disclaimer: FormizoSchema[] = [
  {
    label: "Header (EN)",
    name: "disclaimerDoc.Header.EN",
    variant: "outlined",
    format: "text",
  },
  {
    label: "Text (EN)",
    name: "disclaimerDoc.Content.EN",
    variant: "outlined",
    format: "textarea",
  },
  {
    label: "Header (TC)",
    name: "disclaimerDoc.Header.TC",
    variant: "outlined",
    format: "text",
  },
  {
    label: "Text (TC)",
    name: "disclaimerDoc.Content.TC",
    variant: "outlined",
    format: "textarea",
  },
  {
    label: "Header (SC)",
    name: "disclaimerDoc.Header.SC",
    variant: "outlined",
    format: "text",
  },
  {
    label: "Text (SC)",
    name: "disclaimerDoc.Content.SC",
    variant: "outlined",
    format: "textarea",
  },
];

const schema = {
  restore,
  mwconfig,
  general,
  defaultMessage,
  disclaimer,
  SpamSetting,
  EServiceSetting,
  TimeoutSetting,
  InputFieldSetting,
  SurveySetting,
  RedirectURLSetting,
  LiveAgentSetting,
  MaintenancePageSetting,
  AnswerReviewSetting,
  ErrorMessageSetting,
  SpecialAnswerSetting,
};

export default schema;
